import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";

import { useAuthStore } from "../store/authStore";

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const authPaths = ["/auth/login", "/auth/register", "/auth/refresh"];

const refreshClient: AxiosInstance = axios.create({
  baseURL: "/api",
});

let isRefreshing = false;
let queue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const flushQueue = (error: unknown, token?: string) => {
  queue.forEach(entry => {
    if (error) {
      entry.reject(error);
      return;
    }
    entry.resolve(token ?? "");
  });
  queue = [];
};

const createAuthedClient = (baseURL: string): AxiosInstance => {
  const client = axios.create({ baseURL });

  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    return config;
  });

  client.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as
        | RetryableRequestConfig
        | undefined;
      const refreshToken = useAuthStore.getState().refreshToken;

      if (
        !originalRequest ||
        originalRequest._retry ||
        error.response?.status !== 401 ||
        !refreshToken ||
        authPaths.some(path => originalRequest.url?.includes(path))
      ) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue.push({
            resolve: token => {
              originalRequest.headers.set("Authorization", `Bearer ${token}`);
              resolve(client(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await refreshClient.post("/auth/refresh", {
          refresh_token: refreshToken,
        });

        useAuthStore.getState().setSession({
          user: data.user,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
        });

        flushQueue(null, data.access_token);
        originalRequest.headers.set(
          "Authorization",
          `Bearer ${data.access_token}`
        );
        return client(originalRequest);
      } catch (refreshError) {
        useAuthStore.getState().clearSession();
        flushQueue(refreshError);
        if (window.location.pathname !== "/login") {
          window.location.assign("/login");
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );

  return client;
};

export default createAuthedClient;
